import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

const RecruitingCacheContext = createContext();

const CACHE_DURATION = 30000; // 30 seconds
const STORAGE_KEY = "recruiting_cache";

export const RecruitingCacheProvider = ({ children }) => {
  const [cache, setCache] = useState(() => {
    // Load from localStorage on init
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Check if cache is still valid
        if (
          parsed.timestamp &&
          Date.now() - parsed.timestamp < CACHE_DURATION
        ) {
          return parsed;
        }
      }
    } catch (error) {}
    return {
      leads: null,
      meetings: null,
      timestamp: null,
    };
  });

  // Persist to localStorage whenever cache changes
  useEffect(() => {
    if (cache.timestamp) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
      } catch (error) {}
    }
  }, [cache]);

  const updateLeads = useCallback(leads => {
    setCache(prev => ({
      ...prev,
      leads,
      timestamp: Date.now(),
    }));
  }, []);

  const updateMeetings = useCallback(meetings => {
    setCache(prev => ({
      ...prev,
      meetings,
      timestamp: prev.timestamp || Date.now(),
    }));
  }, []);

  const getCachedLeads = useCallback(() => {
    if (!cache.timestamp) return null;
    const age = Date.now() - cache.timestamp;
    if (age > CACHE_DURATION) return null;
    return cache.leads;
  }, [cache]);

  const getCachedMeetings = useCallback(() => {
    if (!cache.timestamp) return null;
    const age = Date.now() - cache.timestamp;
    if (age > CACHE_DURATION) return null;
    return cache.meetings;
  }, [cache]);

  const isCacheValid = useCallback(() => {
    if (!cache.timestamp) return false;
    return Date.now() - cache.timestamp < CACHE_DURATION;
  }, [cache]);

  const clearCache = useCallback(() => {
    setCache({
      leads: null,
      meetings: null,
      timestamp: null,
    });
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {}
  }, []);

  const value = {
    updateLeads,
    updateMeetings,
    getCachedLeads,
    getCachedMeetings,
    isCacheValid,
    clearCache,
  };

  return (
    <RecruitingCacheContext.Provider value={value}>
      {children}
    </RecruitingCacheContext.Provider>
  );
};

export const useRecruitingCache = () => {
  const context = useContext(RecruitingCacheContext);
  if (!context) {
    throw new Error(
      "useRecruitingCache must be used within RecruitingCacheProvider"
    );
  }
  return context;
};
